import React, { useState, useEffect, useRef } from "react";
import { SmartImage } from "../components/SmartImage";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Camera, 
  Trophy, 
  Users, 
  Globe, 
  Star, 
  Calendar, 
  Award,
  ArrowRight,
  Play
} from "lucide-react";
import { newsAPI, statsAPI, galleryRevealAPI, gallerySponsorsAPI } from "../services/api";
import SEOHead from "../components/SEOHead";
import StructuredData, { getEventSchema, getPrizesSchema, getJudgesSchema } from "../components/StructuredData";
import { getSubmissionImageSrc } from "../utils/imageUtils";
import { SponsorChip } from "../components/SponsorChip";
import { sponsors as sponsorsData } from "../data/sponsors";
import { useSubmissionsOpen } from "../hooks/useSubmissionsOpen";

// Awards-page value lookup by sponsor name (single source of truth).
// Used to order the homepage sponsor banner "biggest first". Unknown names → 0.
const HOMEPAGE_SPONSOR_VALUE = (() => {
  const m = {};
  for (const list of [sponsorsData.liveaboards, sponsorsData.resorts, sponsorsData.equipment]) {
    for (const s of list || []) {
      m[s.name] = s.value ?? s.prizeValue ?? 0;
    }
  }
  return m;
})();

const HomePage = () => {
  const { open: submissionsOpen } = useSubmissionsOpen();
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState({
    total_submissions: 0,
    total_photographers: 0,
    total_spotters: 0,
    countries_represented: 0,
    years_running: 1
  });
  const [categoryStats, setCategoryStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  // Featured Finalists reveal (fetched once per page load).
  // Anti-leak: reads the same public endpoint as /gallery. Only surfaces data
  // when the featured-reveal flag is on (backend gate). Random pick of 3.
  const [finalistsRevealed, setFinalistsRevealed] = useState(false);
  const [randomFinalists, setRandomFinalists] = useState([]);
  const [awardsRevealed, setAwardsRevealed] = useState(false); // Reveal Awards — future switch, unused yet.

  // Global sponsors banner (independent of the reveal flag). Fetched once.
  const [homepageSponsors, setHomepageSponsors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await gallerySponsorsAPI.listAll();
        const list = (res?.data?.sponsors || []).filter(s => s && s.name && s.logo_url);
        // Sort by known Awards value (biggest first), ties → alphabetical.
        list.sort((a, b) => {
          const va = HOMEPAGE_SPONSOR_VALUE[a.name] ?? 0;
          const vb = HOMEPAGE_SPONSOR_VALUE[b.name] ?? 0;
          if (vb !== va) return vb - va;
          return (a.name || "").localeCompare(b.name || "");
        });
        if (!cancelled) setHomepageSponsors(list);
      } catch {
        if (!cancelled) setHomepageSponsors([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await galleryRevealAPI.publicFinalists();
        const data = res?.data || {};
        if (cancelled) return;
        // `revealed` is true iff the backend has actually exposed at least one
        // sub-category. Any race / flag flip mid-flight is handled by the
        // presence of `categories[*].finalists`.
        const cats = Array.isArray(data.categories) ? data.categories : [];
        const flat = [];
        for (const c of cats) {
          for (const f of (c.finalists || [])) {
            // Explicit whitelist. The endpoint already returns only public
            // fields, but we double-guard here so future backend changes
            // cannot leak fields into the homepage.
            flat.push({
              id: f.id,
              image_url: f.image_url,
              title: f.title,
              photographer_name: f.photographer_name,
              category: f.category,
              photographer_category: f.photographer_category,
            });
          }
        }
        if (data.revealed && flat.length > 0) {
          // Fisher-Yates shuffle, then take 3 (or fewer if not enough).
          for (let i = flat.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [flat[i], flat[j]] = [flat[j], flat[i]];
          }
          setRandomFinalists(flat.slice(0, 3));
          setFinalistsRevealed(true);
        } else {
          setRandomFinalists([]);
          setFinalistsRevealed(false);
        }
        // Awards reveal switch — endpoint doesn't expose an `awards_revealed`
        // flag yet; keep the state hook and the render branch inert until
        // the Reveal Awards work replaces the finalist grid on July 13th.
        setAwardsRevealed(false);
      } catch {
        // Network / API error → silently fall back to demo mode.
        if (!cancelled) {
          setRandomFinalists([]);
          setFinalistsRevealed(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide static hero image when React takes over (for smooth transition)
  // Only show it on HomePage, hide on ALL other pages
  useEffect(() => {
    const staticHero = document.getElementById('static-hero-image');
    if (staticHero) {
      staticHero.style.display = 'none';
    }
    return () => {
      // Keep it hidden when leaving - other pages don't need it
      // It will be shown again when returning to HomePage
    };
  }, []);

  // Preload critical images for instant display when scrolling
  useEffect(() => {
    const imagesToPreload = [
      // Category icons
      '/images/categories/macro-icon.png',
      '/images/categories/super-macro-icon.png',
      '/images/categories/nudibranchs-icon.png',
      '/images/categories/crustaceans-icon.png',
      '/images/categories/animal-behavior-icon.png',
      '/images/categories/creative-lighting-icon.png',
      '/images/categories/rare-critters-icon.png',
      '/images/categories/blackwater-bonfire-icon.png',
      // Featured images
      '/images/featured-fish.webp',
      '/images/featured-shrimp.webp',
      '/images/featured-nudibranch.webp'
    ];
    
    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Fetch news articles and stats from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch news - API already returns only published articles
        const newsResponse = await newsAPI.getNews();
        // Backend filters by is_published, so we just use the data directly
        setNews(newsResponse.data);
      } catch (error) {
        console.error('Error fetching news:', error);
        // Fallback to hardcoded articles if API fails
        setNews([
          {
            id: 1,
            title: `Competition Announcement`,
            date: `2025-12-01`,
            content: `We are excited to announce the inaugural Underwater Macro Awards competition for 2026!`,
            image: "/images/news/competition-announcement-2026.jpg"
          },
          {
            id: 2,
            title: "Judging Panel Announced",
            date: `2025-12-15`,
            content: `Meet our prestigious panel of judges for the 2026 competition.`,
            image: "/images/news/judging-panel-2026.jpg"
          },
          {
            id: 3,
            title: `Underwater Macro Awards 2026 Competition Now Open`,
            date: `2026-01-15`,
            content: `The highly anticipated competition is now accepting submissions!`,
            image: "https://images.unsplash.com/photo-1487253096619-4ab6f24d5bbe?w=400&h=300&fit=crop"
          }
        ]);
      }

      try {
        // Fetch real-time stats
        const statsResponse = await statsAPI.getStats();
        setStats(statsResponse.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Keep default stats if API fails
      }

      try {
        // Fetch real-time category stats from gallery API
        const categoryResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery/categories`);
        if (categoryResponse.ok) {
          const categoryData = await categoryResponse.json();
          setCategoryStats(categoryData);
        }
      } catch (error) {
        console.error('Error fetching category stats:', error);
        // Keep empty stats if API fails
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const currentYear = 2026; // First edition of Underwater Macro Awards

  // Featured background images - VRAIES IMAGES DE LA GALERIE
  const featuredImages = [
    {
      src: "/images/featured-fish.webp",
      title: "Thousand eye One parent",
      category: "Marine Behaviours • DSLR",
      status: "Finalist",
      photographer: "Demo Photographer",
      name: "Olivier Martinoo",
      prize: "Prize offered by Planet Dive Anilao"
    },
    {
      src: "/images/featured-shrimp.webp",
      title: "Proud Mama",
      category: "Super-macro • DSLR",
      status: "Winner",
      photographer: "Demo Photographer",
      name: "Olivier Martinoo",
      prize: "Prize offered by Ondina Liveaboard"
    },
    {
      src: "/images/featured-nudibranch.webp", 
      title: "Fluid Flames",
      category: "Nudibranch • DSLR",
      status: "Runner Up",
      photographer: "Demo Photographer",
      name: "Olivier Martinoo",
      prize: "Prize Artemis 1000 Offered by Marelux"
    }
  ];

  // Mock categories for display (until backend provides this)
  const categories = [
    {
      id: 1,
      name: "Macro",
      description: "Close-up photography revealing intricate details of small marine subjects",
      icon: "/images/categories/macro-icon.png",
      submissions: categoryStats["Macro"] || 0
    },
    {
      id: 2,
      name: "Super Macro",
      description: "Extreme close-up photography showcasing the tiniest marine creatures",
      icon: "/images/categories/super-macro-icon.png",
      submissions: categoryStats["Super Macro"] || 0
    },
    {
      id: 3,
      name: "Nudibranchs",
      description: "Colorful sea slugs in all their magnificent diversity",
      icon: "/images/categories/nudibranchs-icon.png",
      submissions: categoryStats["Nudibranchs"] || 0
    },
    {
      id: 4,
      name: "Crustaceans",
      description: "Crabs, shrimp, lobsters and other fascinating crustaceans",
      icon: "/images/categories/crustaceans-icon.png",
      submissions: categoryStats["Crustaceans"] || 0
    },
    {
      id: 5,
      name: "Animal Behaviour",
      description: "Capturing unique behaviors and interactions of marine life",
      icon: "/images/categories/animal-behavior-icon.png",
      submissions: categoryStats["Animal Behaviour"] || 0
    },
    {
      id: 6,
      name: "Creative Lighting",
      description: "Artistic use of light to create stunning underwater compositions",
      icon: "/images/categories/creative-lighting-icon.png",
      submissions: categoryStats["Creative Lighting"] || 0
    },
    {
      id: 7,
      name: "Rare Critters",
      description: "Unusual and rarely photographed marine species",
      icon: "/images/categories/rare-critters-icon.png",
      submissions: categoryStats["Rare Critters"] || 0
    },
    {
      id: 8,
      name: "Blackwater & Bonfire",
      description: "Deep water photography of pelagic species and nocturnal subjects",
      icon: "/images/categories/blackwater-bonfire-icon.png",
      submissions: categoryStats["Blackwater & Bonfire"] || 0
    }
  ];

  // Featured submissions (empty for now, will be populated from API later)
  const featuredSubmissions = [];

  // Active competition (null for now, will be populated from API later)
  const activeCompetition = null;

  return (
    <div className="min-h-screen bg-slate-900 md:bg-transparent">
      <SEOHead 
        title="Home - World Underwater Macro Awards 2026"
        description="Submit your best underwater macro photos and win $41,000+ in prizes. 8 categories, world-class judges. Winners July 14th 2026."
        canonicalPath="/"
      />
      
      {/* Structured Data for SEO - Event, Prizes, Judges */}
      <StructuredData type="event" data={getEventSchema()} />
      <StructuredData type="prizes" data={getPrizesSchema()} />
      <StructuredData type="judges" data={getJudgesSchema()} />
      
      {/* Hero Section with Parallax Effect */}
      <section ref={heroRef} className="relative min-h-[100vh] overflow-hidden flex items-center">
        
        {/* Background Image - Simple and reliable */}
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: 'url(/images/hero-underwater.webp)',
            backgroundSize: 'cover',
            backgroundPosition: '40% 40%',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/40 to-slate-900/30" />
        
        {/* Content Container - SIMPLIFIED */}
        <div className="relative z-50 text-center px-4 sm:px-6 max-w-6xl mx-auto w-full py-20">
          <div className="flex flex-col justify-center items-center space-y-6 sm:space-y-8">
            
            {/* Logo principal - RESPONSIVE avec srcset */}
            <div className="flex-shrink-0">
              <img 
                src="/images/wuma-logo-transparent.webp"
                srcSet="/images/wuma-logo-transparent.webp 600w"
                sizes="(max-width: 640px) 256px, (max-width: 768px) 320px, (max-width: 1024px) 384px, (max-width: 1280px) 500px, 600px"
                alt="World Underwater Macro Awards 2026 Logo"
                width="600"
                height="600"
                className="w-64 sm:w-80 md:w-96 lg:w-[500px] xl:w-[600px] h-auto drop-shadow-2xl mx-auto"
                loading="eager"
              />
            </div>
            
            {/* H1 + H2 - SEO Optimized and Visible */}
            <div className="max-w-4xl px-2 sm:px-4">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-3 sm:mb-4 leading-tight font-bold drop-shadow-2xl">
                World Underwater Macro Awards 2026
              </h1>
              <h2 className="text-sm sm:text-base md:text-lg lg:text-xl text-orange-300 mb-3 sm:mb-4 leading-normal md:drop-shadow-lg font-semibold">
                First Competition Dedicated to Underwater Macro Photography
              </h2>
              
              <p className="text-xs sm:text-sm md:text-base text-orange-200 leading-normal md:drop-shadow-lg">
                Celebrating excellence in macro and super macro photography
              </p>
            </div>
            
            {/* Boutons - RESPONSIVE */}
            <div className="flex flex-col sm:flex-row gap-4">
              {submissionsOpen && (
                <Link to="/submit">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  >
                    <Camera className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                    Submit Your Photo
                  </Button>
                </Link>
              )}
              
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                >
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  Spotters Registration
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 sm:gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-400 mb-2">
                {stats.total_submissions?.toLocaleString() || 0}
              </div>
              <div className="text-sm sm:text-base text-blue-200">Total Submissions</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-cyan-400 mb-2">
                {stats.total_photographers || 0}
              </div>
              <div className="text-sm sm:text-base text-blue-200">Photographers</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-400 mb-2">
                {stats.total_spotters || 0}
              </div>
              <div className="text-sm sm:text-base text-blue-200">Spotters</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-400 mb-2">
                {stats.countries_represented || 0}
              </div>
              <div className="text-sm sm:text-base text-blue-200">Countries</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-cyan-400 mb-2">
                {stats.years_running || 1}
              </div>
              <div className="text-sm sm:text-base text-blue-200">Year Running</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured section — Winners demo (before reveal) → real Finalists (after reveal) */}
      <section className="py-20" data-testid="home-featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge
              variant="outline"
              className="bg-blue-900/50 text-blue-300 border-blue-400/50 mb-4"
              data-testid="home-featured-chip"
            >
              <Trophy className="w-4 h-4 mr-2" />
              {finalistsRevealed ? "Featured Finalists" : "Featured Winners"}
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Award Winning Macro Photography
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Discover The finest underwater close-up imagery
            </p>
            {!finalistsRevealed && (
              <h3 className="text-2xl sm:text-3xl font-bold text-orange-400 max-w-3xl mx-auto mt-4">
                Demo Gallery
              </h3>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {finalistsRevealed ? (
              // Reveal-on mode: real finalists (public fields only, no rank/prize).
              randomFinalists.map((f) => (
                <Card
                  key={f.id}
                  className="group bg-slate-800/50 border-blue-900/20 hover:border-blue-400/50 hover:scale-105 hover:shadow-2xl transition-transform duration-300"
                  data-testid={`home-finalist-card-${f.id}`}
                >
                  <div className="relative overflow-hidden rounded-t-lg">
                    <SmartImage
                      src={f.image_url}
                      alt={f.title || "Finalist"}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge
                        className="bg-blue-500/90 text-white font-semibold"
                        data-testid="home-finalist-badge"
                      >
                        <Award className="w-3 h-3 mr-1" />
                        Finalist
                      </Badge>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-white group-hover:text-blue-400 transition-colors">
                      {f.title || "Untitled"}
                    </CardTitle>
                    <CardDescription className="text-blue-200">
                      {f.category}
                      {f.photographer_category ? ` • ${f.photographer_category === "Compact Camera User" ? "Compact" : "Open"}` : ""}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-full border-2 border-blue-400 bg-blue-600" />
                      <span className="text-blue-200 font-medium text-sm">
                        {f.photographer_name || "Anonymous"}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : featuredSubmissions.length > 0 ? (
              featuredSubmissions.map((submission) => (
                <Card key={submission.id} className="group bg-slate-800/50 border-blue-900/20 hover:border-blue-400/50 hover:scale-105 hover:shadow-2xl transition-transform duration-300">
                  <div className="relative overflow-hidden rounded-t-lg">
                    <SmartImage
                      src={getSubmissionImageSrc(submission)}
                      alt={submission.title}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-yellow-500/90 text-yellow-900 font-semibold">
                        <Award className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-white group-hover:text-blue-400 transition-colors">
                      {submission.title}
                    </CardTitle>
                    <CardDescription className="text-blue-200">
                      {submission.category} • {submission.location}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <img 
                          src={submission.photographer.profile_picture || "https://images.unsplash.com/photo-1494790108755-2616b612b776?w=150&h=150&fit=crop&crop=face"}
                          alt={submission.photographer.name}
                          className="w-8 h-8 rounded-full border-2 border-blue-400"
                        />
                        <span className="text-blue-200 font-medium">
                          {submission.photographer.name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 text-blue-300">
                        <Star className="w-4 h-4 fill-current" />
                        <span>{submission.likes}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              // Nouvelles vraies images de la galerie avec srcset
              [0, 1, 2].map((index) => (
                <Card key={index} className="group bg-slate-800/50 border-blue-900/20 hover:border-blue-400/50 hover:scale-105 hover:shadow-2xl transition-transform duration-300">
                  <div className="relative overflow-hidden rounded-t-lg">
                    <img 
                      src={featuredImages[index].src}
                      srcSet={`${featuredImages[index].src} 800w`}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      alt={featuredImages[index].title}
                      width="800"
                      height="533"
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className={`text-white font-semibold ${
                        featuredImages[index].status === 'Winner' ? 'bg-yellow-500/90' : 
                        featuredImages[index].status === 'Runner Up' ? 'bg-orange-500/90' :
                        'bg-blue-500/90'
                      }`}>
                        <Award className="w-3 h-3 mr-1" />
                        {featuredImages[index].status}
                      </Badge>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-white group-hover:text-blue-400 transition-colors">
                      {featuredImages[index].title}
                    </CardTitle>
                    <CardDescription className="text-blue-200">
                      {featuredImages[index].category}
                    </CardDescription>
                    {featuredImages[index].prize && (
                      <div className="mt-2">
                        <Link 
                          to={`/awards#${featuredImages[index].prize
                            .replace(/Prize (offered by |Artemis 1000 Offered by )/i, '')
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, '-')
                            .replace(/^-|-$/g, '')}`}
                          className="inline-block"
                        >
                          <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs cursor-pointer transition-colors">
                            {featuredImages[index].prize}
                          </Badge>
                        </Link>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full border-2 border-blue-400 bg-blue-600"></div>
                        <div className="flex flex-col">
                          <span className="text-blue-200 font-medium text-sm">
                            {featuredImages[index].photographer}
                          </span>
                          <span className="text-blue-300 text-sm">
                            {featuredImages[index].name}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 text-blue-300">
                        <Star className="w-4 h-4 fill-current" />
                        <span>{Math.floor(Math.random() * 50) + 10}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            {/* Global sponsors banner — independent of the reveal flag,
                homepage-only. Do NOT reuse elsewhere: per-category galleries
                have exclusive sponsor mappings. */}
            {homepageSponsors.length > 0 && (
              <div
                data-testid="home-sponsors-banner"
                className="mb-10 border-y border-white/10 bg-gradient-to-r from-slate-950/40 via-slate-900/40 to-slate-950/40 py-8 px-4"
              >
                <div className="text-[11px] uppercase tracking-widest text-orange-300/70 mb-5">
                  Our Sponsors &amp; Partners
                </div>
                <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 max-w-6xl mx-auto">
                  {homepageSponsors.map((s, idx) => (
                    <SponsorChip
                      key={`${s.name}-${idx}`}
                      sponsor={s}
                      testid={`home-sponsor-chip-${idx}`}
                    />
                  ))}
                </div>
                <div className="mt-5">
                  <Link
                    to="/awards"
                    data-testid="home-sponsors-see-all-link"
                    className="text-sm text-blue-300 hover:text-blue-200 underline underline-offset-4 decoration-blue-400/40"
                  >
                    See all sponsors &amp; prizes
                  </Link>
                </div>
              </div>
            )}

            <Link to="/gallery">
              <Button
                size="lg"
                data-testid="home-featured-cta"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                {finalistsRevealed ? "View All Finalists" : "View All Winners"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="bg-blue-900/50 text-blue-300 border-blue-400/50 mb-4">
              <Camera className="w-4 h-4 mr-2" />
              Competition Categories
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Eight Macro's Categories
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              From macro to creative lighting, find the perfect category for your underwater masterpiece
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <Card key={category.id} className="group bg-slate-800/50 border-blue-900/20 hover:border-blue-400/50 hover:scale-105 transition-transform duration-300">
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 flex items-center justify-center bg-slate-700/50 rounded-full overflow-hidden">
                      <img 
                        src={category.icon}
                        srcSet={`${category.icon} 112w`}
                        sizes="(max-width: 640px) 80px, (max-width: 768px) 96px, 112px"
                        alt={`${category.name} icon`}
                        width="112"
                        height="112"
                        className="w-full h-full object-cover scale-95"
                      />
                    </div>
                  </div>
                  <CardTitle className="text-white group-hover:text-blue-400 transition-colors">
                    <h3 className="text-xl font-bold">{category.name}</h3>
                  </CardTitle>
                  <CardDescription className="text-blue-200">
                    {category.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Badge variant="secondary" className="bg-blue-900/50 text-blue-300">
                    {category.submissions} submissions
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* News & Updates */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="bg-blue-900/50 text-blue-300 border-blue-400/50 mb-4">
              <Calendar className="w-4 h-4 mr-2" />
              Latest News
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Stay Updated
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Get the latest news about competitions, winners, and underwater photography
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.map((article) => (
              <Card key={article.id} className="group bg-slate-800/50 border-blue-900/20 hover:border-blue-400/50 hover:scale-105 transition-transform duration-300">
                <div className="relative overflow-hidden rounded-t-lg bg-slate-900">
                  <img 
                    src={article.image}
                    srcSet={`${article.image} 600w`}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    alt={article.title}
                    width="600"
                    height="450"
                    className="w-full h-48 object-contain group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-white group-hover:text-blue-400 transition-colors">
                    {article.title}
                  </CardTitle>
                  <CardDescription className="text-blue-200">
                    {new Date(article.date).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-200">{article.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-cyan-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Dive In?
          </h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-8">
            Join hundred of underwater Macro photographers in the first world's competition dedicated to Underwater Macro
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-blue-50 px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              >
                <Users className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                Register Now
              </Button>
            </Link>
            {activeCompetition && (
              <Link to={`/competition/${activeCompetition.year}`}>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold rounded-full transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
                >
                  <Globe className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  Learn More
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;